import { useEffect, useSyncExternalStore } from "react";

/**
 * Tiny external store counting how many mounted screens have asked for the
 * intake entry point (tab/pill) to be hidden. The entry point (see
 * `IntakeEntryPoint`) subscribes and renders nothing while the count is
 * above zero.
 */
let hideRequests = 0;
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): boolean {
  return hideRequests > 0;
}

/** Whether any screen currently asks for the intake entry point to be hidden. */
export function useIntakePanelEntryHidden(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Hides the intake entry point while `active` is true (for example on the
 * mobile calendar, where a floating tab would sit over the timeline).
 * Restores it on unmount or when `active` flips back to false.
 */
export function useHideIntakePanelEntry(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    hideRequests += 1;
    emit();
    return () => {
      hideRequests -= 1;
      emit();
    };
  }, [active]);
}
